import React, { useState, useEffect } from 'react';
import { Send, Mail, MessageSquare, User, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const ContactForm = ({ user, onSubmitSuccess, initialOpen = false }) => {
  // Get the primary display name (Flickr account or fallback to email)
  const displayName = user?.connectedAccounts?.find(acc => acc.provider === 'flickr')?.displayName || 
                     user?.email?.split('@')[0] || '';

  const [formData, setFormData] = useState({
    name: displayName,
    email: user?.email || '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(initialOpen);

  // Update form data when user prop changes
  useEffect(() => {
    const newDisplayName = user?.connectedAccounts?.find(acc => acc.provider === 'flickr')?.displayName || 
                          user?.email?.split('@')[0] || '';
    setFormData(prev => ({
      ...prev,
      name: newDisplayName || prev.name,
      email: user?.email || prev.email
    }));
  }, [user?.connectedAccounts, user?.email]);

  // Update isOpen state when initialOpen changes (for modal usage)
  useEffect(() => {
    setIsOpen(initialOpen);
  }, [initialOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Basic email validation (only for non-authenticated users)
    if (!user?.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error('Please enter a valid email address');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      await axios.post('/api/contact', formData, {
        withCredentials: true
      });
      
      toast.success('Thank you! Your message has been sent successfully.');
      
      // Call success callback if provided (for modal closing)
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
      
      // Clear form and close it (but keep user name and email)
      const newDisplayName = user?.connectedAccounts?.find(acc => acc.provider === 'flickr')?.displayName || 
                            user?.email?.split('@')[0] || '';
      setFormData({
        name: newDisplayName,
        email: user?.email || '',
        subject: '',
        message: ''
      });
      setIsOpen(false);
    } catch (error) {
      console.error('Error sending contact form:', error);
      toast.error(error.response?.data?.error || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div 
        className="px-6 py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                <Mail className="w-4 h-4 text-primary-600" />
              </div>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">Contact Us</h3>
              <p className="text-sm text-gray-500">
                {isOpen ? 'Share your feedback, questions, or suggestions' : 'Click to share your feedback, questions, or suggestions'}
              </p>
            </div>
          </div>
          <div className="flex-shrink-0">
            {isOpen ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
      </div>
      
      {isOpen && (
        <div className="overflow-hidden transition-all duration-300 ease-in-out">
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
        <div className={`grid grid-cols-1 ${!user?.email ? 'md:grid-cols-2' : ''} gap-4`}>
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              <User className="inline w-4 h-4 mr-1" />
              Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Your full name"
            />
            {displayName && (
              <p className="mt-1 text-xs text-gray-500">Pre-filled from your account. You can edit this if needed.</p>
            )}
          </div>
          
          {/* Only show email field for non-authenticated users */}
          {!user?.email && (
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="inline w-4 h-4 mr-1" />
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="your.email@example.com"
              />
            </div>
          )}
          
          {/* Show verified email indicator for authenticated users */}
          {user?.email && (
            <div className="md:col-span-1">
              <div className="flex items-center px-3 py-2 bg-green-50 border border-green-200 rounded-md">
                <Mail className="w-4 h-4 text-green-600 mr-2" />
                <span className="text-sm text-green-700 font-medium">
                  Using verified email: {user.email}
                </span>
              </div>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
            Subject
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="What is this about?"
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
            <MessageSquare className="inline w-4 h-4 mr-1" />
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            value={formData.message}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-vertical"
            placeholder="Tell us what's on your mind..."
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Message
              </>
            )}
          </button>
        </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ContactForm;
